import { i18next } from './i18n';

/**
 * Een label of naam in de taal van de lezer, met Nederlands als terugval.
 *
 * **Eén hulpfunctie, en niet overal `label.nl`.** Dat laatste stond op negen plaatsen in het
 * beheerportaal, en het betekende dat een Franstalige beheerder zijn eigen Franse veldlabels niet
 * terugzag - ook niet als iemand ze had ingevuld. De data was meertalig; de schermen niet.
 *
 * De terugval is Nederlands en niet "de eerste de beste": een veld waarvan alleen het Franse label
 * ingevuld is, is in het Nederlands nog altijd beter te herkennen aan zijn sleutel dan aan een
 * taal die de lezer niet spreekt. Is er helemaal niets, dan geeft deze functie `null` terug en
 * kiest de aanroeper wat er in de plaats komt - meestal de sleutel.
 */
export function localized(
  value: Record<string, string | undefined> | null | undefined,
  language = i18next.language,
): string | null {
  if (!value) return null;
  const kort = language.split('-')[0] ?? language;
  return value[language] ?? value[kort] ?? value.nl ?? null;
}

/**
 * De taal waarin datums en getallen opgemaakt worden.
 *
 * `i18n.language` en niet `'nl-BE'`, en ook niet de standaard van de browser: de eerste is een
 * vaste Belgische opmaak voor een Franstalige lezer, de tweede hangt af van het toestel en kan
 * dus per medewerker verschillen terwijl ze hetzelfde scherm bekijken.
 */
export function uiLocale(): string {
  return i18next.language || 'nl';
}

/** Een datum zonder tijd: "7 okt. 2026". */
export function formatDate(
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' },
): string {
  if (!value) return '';
  const datum = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(datum.getTime())) return '';
  return new Intl.DateTimeFormat(uiLocale(), options).format(datum);
}

/** Een datum met tijd: "7 okt. 2026, 10:24". */
export function formatDateTime(value: string | Date | null | undefined): string {
  return formatDate(value, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Alleen de tijd: "10:24". */
export function formatTime(value: string | Date | null | undefined): string {
  return formatDate(value, { hour: '2-digit', minute: '2-digit' });
}

/** Een getal in de opmaak van de lezer: duizendtallen en komma's volgen zijn taal. */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(uiLocale(), options).format(value);
}
